import { useEffect, useState } from 'react';
import type { SharedRecordNote } from '@oneup/shared';
import { Switch } from '@command-center/ui-system';
import { SectionHeading } from './record-presentation';
import { RecordNotes } from './record-notes';

/**
 * The one decision a reviewer makes on a call: is this a Lead or not?
 *
 * This replaces the Assessment tab's three-dropdown editor. That editor asked
 * the reviewer to set Caller relationship, Call type, and Lead eligibility —
 * three answers to produce one outcome, on a tab most people never opened,
 * which meant real leads sat unreachable behind a control nobody found.
 *
 * Calls arrive with this already answered (see `autoLeadEligibility` in
 * switchboard-sdk): spam, solicitors, wrong numbers, silence, and calls with no
 * transcript come in OFF; everything else comes in ON. The toggle exists to
 * correct the exceptions, so the common case is zero clicks.
 *
 * Call type stays a read-only badge on the record. It describes the call; it is
 * not a decision, and making it editable invited people to reclassify a call in
 * order to change what they were allowed to do with it.
 */
export function LeadEligibilityToggle({
  eligible,
  onChange,
  automatic,
}: {
  eligible: boolean;
  onChange: (eligible: boolean) => Promise<void>;
  /** True while no one has overridden the automatic answer. */
  automatic?: boolean;
}) {
  const [value, setValue] = useState(eligible);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  useEffect(() => setValue(eligible), [eligible]);

  const toggle = async (next: boolean) => {
    setValue(next);
    setBusy(true);
    setError(undefined);
    try {
      await onChange(next);
    } catch (cause) {
      setValue(!next);
      setError(cause instanceof Error ? cause.message : 'The change could not be saved.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg border bg-muted/20 p-3">
      <label className="flex items-center justify-between gap-3">
        <span className="min-w-0">
          <span className="block text-sm font-medium text-foreground">Track as a Lead</span>
          <span className="block text-xs text-muted-foreground">
            {value
              ? 'This call can be turned into a Lead or linked to one.'
              : 'Set automatically for spam, solicitors, wrong numbers, and silent calls.'}
            {automatic && ' Set automatically — change it any time.'}
          </span>
        </span>
        <Switch
          checked={value}
          disabled={busy}
          onCheckedChange={(next: boolean) => void toggle(next)}
          aria-label="Track this call as a Lead"
        />
      </label>
      {error && (
        <p className="mt-2 text-sm text-destructive" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

export function SharedRecordManagement({
  notes = [],
  archived = false,
  onAddNote,
  onArchive,
  onRestore,
  notesReadOnly = false,
}: {
  notes?: SharedRecordNote[];
  archived?: boolean;
  onAddNote: (body: string) => Promise<void>;
  onArchive: () => Promise<void>;
  onRestore: () => Promise<void>;
  /** True where an Action Center on Overview already owns note entry. */
  notesReadOnly?: boolean;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  async function run(operation: () => Promise<void>) {
    setBusy(true);
    setError(undefined);
    try {
      await operation();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'The change could not be saved.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-5 rounded-lg border bg-muted/20 p-4">
      {/*
        Notes render through the shared component rather than inline, so calls
        and form entries stay identical to contacts and organizations. It owns
        its own saving/error state — `run` here is for archiving.
      */}
      <RecordNotes
        notes={notes}
        onAddNote={onAddNote}
        busy={busy}
        readOnly={notesReadOnly}
        description={
          notesReadOnly
            ? 'Notes on this record, newest first. Add one from Action needed on Overview.'
            : undefined
        }
      />

      <section className="border-t pt-4">
        <SectionHeading>Record</SectionHeading>
        <button
          type="button"
          disabled={busy}
          onClick={() => run(archived ? onRestore : onArchive)}
          className="rounded-md border px-3 py-2 text-sm font-medium disabled:opacity-50"
        >
          {archived ? 'Restore record' : 'Archive record'}
        </button>
      </section>

      {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
    </div>
  );
}
